import {
  CommonModule,
  NgOptimizedImage,
  isPlatformBrowser,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  PLATFORM_ID,
  TransferState,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { AmbassadorLandingSectionComponent } from '@app/ambassador-landing-section/ambassador-landing-section.component';
import { SSR_DATA } from '@app/app-initializer';
import { CardsComponent } from '@app/cards/cards.component';
import { ChannelManagersComponent } from '@app/channel-managers/channel-managers.component';
import { CreditsSectionComponent } from '@app/credits-section/credits-section.component';
import { DownloadComponent } from '@app/download/download.component';
import { EnvironmentService } from '@app/environment.service';
import { ExploreMobileComponent } from '@app/explore-mobile/explore-mobile.component';
import { ExploreComponent } from '@app/explore/explore.component';
import { FirstSectionLandingComponent } from '@app/first-section-landing/first-section-landing.component';
import { HeaderComponent } from '@app/header/header.component';
import { JourneysLandingSectionComponent } from '@app/journeys-landing-section/journeys-landing-section.component';
import { PageComponent } from '@app/page/page.component';
import { PipesModule } from '@app/pipes/pipes.module';
import { PromoScrollerComponent } from '@app/promo-scroller/promo-scroller.component';
import { SearchBarComponent } from '@app/search-bar/search-bar.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { QrCodeCtaComponent } from 'qr-code-cta/qr-code-cta.component';
import {
  BehaviorSubject,
  Subject,
  catchError,
  map,
  of,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { DirectivesModule } from '../directives/directives.module';
import { ApiService, FeaturedListing } from '../services/api/api.service';
import { MainModule } from './main.module';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    RouterModule,
    CardsComponent,
    NgOptimizedImage,
    DownloadComponent,
    ExploreComponent,
    MainModule,
    ExploreMobileComponent,
    PipesModule,
    DirectivesModule,
    ChannelManagersComponent,
    QrCodeCtaComponent,
    PromoScrollerComponent,
    PageComponent,
    HeaderComponent,
    JourneysLandingSectionComponent,
    SearchBarComponent,
    CreditsSectionComponent,
    AmbassadorLandingSectionComponent,
    FirstSectionLandingComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    ngSkipHydration: 'true',
  },
})
export class MainComponent {
  @ViewChild(ExploreComponent) exploreComponent!: ExploreComponent;
  @ViewChild(ExploreMobileComponent)
  exploreMobileComponent!: ExploreMobileComponent;
  public featured$ = new BehaviorSubject<FeaturedListing[]>([]);
  public skeleton = true;
  public desktop = new BehaviorSubject(true);

  public where: any = null;
  public city: any = null;

  private destroyed = new Subject<void>();

  public pwaUrl = this.environment.pwaUrl;

  constructor(
    private apiService: ApiService,

    @Inject(PLATFORM_ID) private platformId: string,

    private transferState: TransferState,
    private readonly environment: EnvironmentService
  ) {}

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    if (this.isBrowser) {
      const ssrData = this.transferState.get(SSR_DATA, {});
      if (ssrData?.['FEATURED']) {
        this.featured$.next(
          ssrData['FEATURED'].data.map((hostel: FeaturedListing) => {
            return {
              ...hostel,
              propertyImages: hostel.propertyImages.sort((a, b) => {
                if (a.isCoverImage) {
                  return -1;
                }
                return 1;
              }),
            };
          }) as any[]
        );
        this.skeleton = false;
      } else {
        this.apiService.properties
          .pipe(
            switchMap((properties) => {
              return this.apiService.getFeatured().pipe(
                map((_) => {
                  return _.data.data.map((hostel) => {
                    return {
                      ...hostel,
                      propertyImages: hostel.propertyImages.sort((a, b) => {
                        if (a.isCoverImage) {
                          return -1;
                        }
                        return 1;
                      }),
                    };
                  });
                })
              );
            }),
            tap((properties) => {
              this.skeleton = false;
              this.featured$.next(properties as any[]);
            }),
            takeUntil(this.destroyed),
            catchError((err) => {
              console.log(
                'error initializing properties in main:',
                err.message
              );
              return of([]);
            })
          )
          .subscribe();
      }
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  public update(properties: any[]) {
    this.featured$.next(properties);
  }
}
